export default class {
    constructor ($interval, $state, $translate, toastr, localStorageService, jwtHelper, ModalService, AuthResource) {
        this.$interval = $interval;
        this.$state = $state;
        this.$translate = $translate;
        this.toastr = toastr;
        this.localStorageService = localStorageService;
        this.jwtHelper = jwtHelper;
        this.ModalService = ModalService;
        this.AuthResource = AuthResource;
        this.tokenKey = 'coursy:auth:public:token';
    }

    isExpired (token) {
        return this.hasToken() ? this.jwtHelper.isTokenExpired(token) : true;
    }

    isGoingToExpire (token) {
        const decoded = this.decode(token);
        if (decoded) {
            const difference = parseInt(decoded.exp.toString().concat('000'), 10) - Date.now();

            const minutesDifference = Math.floor(difference / 1000 / 60);
            if (minutesDifference < 10) {
                return true;
            }
        }
        return false;
    }

    decode (token) {
        return this.hasToken() ? this.jwtHelper.decodeToken(token) : false;
    }

    clear () {
        this.localStorageService.remove(this.tokenKey);
    }

    hasToken () {
        return !!this.token;
    }

    get token () {
        return this.localStorageService.get(this.tokenKey);
    }

    set token (token) {
        return this.localStorageService.set(this.tokenKey, token);
    }

    startTokenExpirationWatcher () {
        return this.$interval(() => {
            if (this.isExpired(this.token)) {
                const companySlug = this.getCompanySlug();
                this.clear();
                if (!companySlug) {
                    // force reload it company slug is not found from token
                    window.location.reload(true);
                }
                else {
                    // TODO: this should set a permanent msg instead of toastr on login page
                    this.toastr.error(this.$translate.instant('Your session has expired. Please log in again.'));
                    this.$state.go('home', { company: companySlug });
                    window.location.reload(true);
                }
            }
        }, 1000);
    }

    startTokenWatcher () {
        const stop = this.$interval(() => {
            if (this.isGoingToExpire(this.token)) {
                this.$interval.cancel(stop);
                const mgs = this.$translate.instant('Session is about to expire. To keep the session up, please press the button or refresh the page.');
                this.ModalService.confirm(mgs, 'medium').then(() => {
                    this.AuthResource.renewToken();
                });
            }
        }, 1000);
        return stop;
    }

    getSiteCount () {
        return this.hasToken() ? parseInt(this.decode(this.token).context.siteCount, 10) : null;
    }

    getCompanyId () {
        return this.hasToken() ? this.decode(this.token).context.companyId : null;
    }

    getLdUserId () {
        return this.hasToken() ? this.decode(this.token).context.user.wp_id : null;
    }

    getCompanySlug () {
        return this.hasToken() ? this.decode(this.token).context.companySlug : null;
    }

    getCompanySkin () {
        const skin = this.hasToken() ? this.decode(this.token).context.companySkin : null;
        if (!skin) {
            return SEED_CORE.ENV.SKIN;
        }
        return skin;
    }

    getCompanyLanguage (inLowerCase) {
        const language = this.hasToken() ? this.decode(this.token).context.language : '';
        return inLowerCase === true ? language.toLowerCase() : language;
    }

    getCompanySupportedLanguages (inLowerCase) {
        const supportedLanguages = this.hasToken() ? this.decode(this.token).context.companyLanguages : [];

        if (inLowerCase) {
            return supportedLanguages.map((language) => language.toLowerCase());
        }

        return supportedLanguages;
    }

    getCompanyTimezone () {
        return this.hasToken() ? this.decode(this.token).context.timezone : '';
    }

    getCompanyName () {
        return this.hasToken() ? this.decode(this.token).context.companyName : '';
    }

    getCompanyDefaultCountry () {
        return this.hasToken() ? this.decode(this.token).context.defaultCountry : '';
    }

    getCompanyTrainingPortalUrl () {
        return this.hasToken() ? this.decode(this.token).context.trainingPortalUrl : '';
    }

    hasPrivilege (privilege) {
        if (!this.hasToken()) {
            return false;
        }
        const privileges = this.decode(this.token).context.privileges;
        const privilegesArr = Object.keys(privileges).map((key) => {
            return privileges[key];
        });

        return privilegesArr.indexOf(privilege) !== -1;
    }

    getUser () {
        return this.hasToken() ? this.decode(this.token).context.user : null;
    }

    getActiveSiteId () {
        return this.hasToken() ? this.decode(this.token).context.activeSiteId : null;
    }

    getActiveLanguage (inLowerCase) {
        const userLanguage = this.hasToken() ? this.decode(this.token).context.user.admin_language : '';
        // if user language is set we choose that, if not then we go with company language
        const language = userLanguage === '' || userLanguage === undefined ? this.getCompanyLanguage() : userLanguage;
        return inLowerCase === true ? language.toLowerCase() : language;
    }

    isCompanyMagnum () {
        return this.getCompanySlug() === 'magnum';
    }

    isScormCloudEnabled () {
        return this.hasToken() ? this.decode(this.token).context.isScormCloudEnabled : null;
    }

    isFormModuleEnabled () {
        return this.hasToken() ? this.decode(this.token).context.isFormModuleEnabled : false;
    }

    isFormEvaluationModuleEnabled () {
        return this.hasToken() ? this.decode(this.token).context.isFormEvaluationModuleEnabled : false;
    }

    isFormSurveyModuleEnabled () {
        return this.hasToken() ? this.decode(this.token).context.isFormSurveyModuleEnabled : false;
    }

    isPerformanceReviewModuleEnabled () {
        return this.hasToken() ? this.decode(this.token).context.isPerformanceReviewModuleEnabled : false;
    }

    isLicencePointsModuleEnabled () {
        return this.hasToken() ? this.decode(this.token).context.isLicencePointsModuleEnabled : false;
    }

    isOptionalCoursesModuleEnabled () {
        return this.hasToken() ? this.decode(this.token).context.isOptionalCoursesModuleEnabled : false;
    }

    isBudgetModuleEnabled () {
        return this.hasToken() ? this.decode(this.token).context.isBudgetModuleEnabled : false;
    }

    isCompetencesModuleEnabled () {
        return this.hasToken() ? this.decode(this.token).context.isCompetencesModuleEnabled : false;
    }

    isWebinarModuleEnabled () {
        return this.hasToken() ? this.decode(this.token).context.isWebinarModuleEnabled : false;
    }

    isTasklistModuleEnabled () {
        return this.hasToken() ? this.decode(this.token).context.isTasklistModuleEnabled : false;
    }

    isEventsModuleEnabled () {
        return this.hasToken() ? this.decode(this.token).context.isEventsModuleEnabled : false;
    }

    isSkillsModuleEnabled () {
        return this.hasToken() ? this.decode(this.token).context.isSkillsModuleEnabled : false;
    }

    isAnalyticsModuleEnabled () {
        return this.hasToken() ? this.decode(this.token).context.isAnalyticsModuleEnabled : false;
    }

    isAssistantModuleEnabled () {
        return this.hasToken() ? this.decode(this.token).context.isAssistantModuleEnabled : false;
    }

    isGuidanceModuleEnabled () {
        return this.hasToken() ? this.decode(this.token).context.isGuidanceModuleEnabled : false;
    }

    isExternalParticipationModuleEnabled () {
        return this.hasToken() ? this.decode(this.token).context.isExternalParticipationModuleEnabled : false;
    }

    isTrainingCompany () {
        return this.hasToken() ? this.decode(this.token).context.isTrainingCompany : false;
    }

    getSiteAccessForAllSites () {
        return this.hasToken() ? this.decode(this.token).context.siteAccessForAllSites : false;
    }

    getCompanyLanguages () {
        return this.hasToken() ? this.decode(this.token).context.languages : false;
    }
}



// WEBPACK FOOTER //
// ./src/app/services/token/index.js